import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  FaUsers,
  FaSearch,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaChevronDown,
  FaChevronUp
} from "react-icons/fa";

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedKey, setSelectedKey] = useState("");
  const ordersPanel = useRef(null);

  const orderStatus = (ord) => {
    const label = ord.status_label || "Placed";
    if (label === "Cancelled" || label === "Refunded") return { label, badge: "admin-badge-danger" };
    if (label === "Delivered") return { label, badge: "admin-badge-success" };
    if (label === "Shipped") return { label, badge: "admin-badge-info" };
    return { label, badge: "admin-badge-warning" };
  };

  const paymentLabel = (mode) => {
    if (mode === "COD") return "Cash on Delivery";
    if (mode === "BT") return "Bank transfer";
    return mode || "—";
  };

  useEffect(() => {
    document.title = "Customers & Shoppers — Admin";
    axios.get("/api/view-orders").then((res) => {
      if (res.data.status === 200) {
        const orders = res.data.orders || [];
        const customerMap = {};

        orders.forEach((ord) => {
          const email = (ord.email || "").trim().toLowerCase();
          const key = email || `phone-${ord.phone}` || `guest-${ord.id}`;

          const orderTotal = Number(ord.total_amount || 0);

          if (!customerMap[key]) {
            customerMap[key] = {
              key,
              name: `${ord.firstname || ""} ${ord.lastname || ""}`.trim() || "Guest Customer",
              email: ord.email || "No Email",
              phone: ord.phone || "No Phone",
              city: ord.city || "Pakistan",
              isRegistered: !!ord.user_id,
              totalOrders: 0,
              totalSpent: 0,
              lastOrderDate: ord.created_at,
              orders: [],
            };
          }

          customerMap[key].orders.push(ord);
          customerMap[key].totalOrders += 1;
          customerMap[key].totalSpent += orderTotal;
          if (new Date(ord.created_at) > new Date(customerMap[key].lastOrderDate)) {
            customerMap[key].lastOrderDate = ord.created_at;
            customerMap[key].name = `${ord.firstname || ""} ${ord.lastname || ""}`.trim() || customerMap[key].name;
            customerMap[key].phone = ord.phone || customerMap[key].phone;
            customerMap[key].city = ord.city || customerMap[key].city;
          }
        });

        Object.values(customerMap).forEach((cust) => {
          cust.orders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        });

        setCustomers(Object.values(customerMap));
      }
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, []);

  const filtered = customers.filter((cust) => {
    const q = searchQuery.toLowerCase();
    return (
      cust.name.toLowerCase().includes(q) ||
      cust.email.toLowerCase().includes(q) ||
      cust.phone.toLowerCase().includes(q) ||
      cust.city.toLowerCase().includes(q)
    );
  });

  const totalSpentAll = customers.reduce((acc, c) => acc + c.totalSpent, 0);

  const openCustomer = (cust) => {
    const nextKey = selectedKey === cust.key ? "" : cust.key;
    setSelectedKey(nextKey);
    if (nextKey) {
      window.setTimeout(() => {
        if (ordersPanel.current) {
          ordersPanel.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 0);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: "var(--admin-text-main)" }}>
            Customer Directory ({customers.length})
          </h4>
          <p className="text-muted mb-0" style={{ fontSize: "13.5px" }}>
            Overview of registered users and guest shoppers across all store orders
          </p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="row g-3 mb-4">
        <div className="col-12 col-md-4">
          <div className="admin-stat-card">
            <span className="admin-stat-label">Total Unique Shoppers</span>
            <div className="admin-stat-value text-primary">{customers.length}</div>
            <div className="admin-stat-footer text-muted">Guest & registered customers</div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="admin-stat-card">
            <span className="admin-stat-label">Total Customer Spend</span>
            <div className="admin-stat-value text-success">Rs. {totalSpentAll.toLocaleString()}</div>
            <div className="admin-stat-footer text-muted">Gross lifetime volume</div>
          </div>
        </div>
        <div className="col-12 col-md-4">
          <div className="admin-stat-card">
            <span className="admin-stat-label">Average Spend / Customer</span>
            <div className="admin-stat-value text-dark">
              Rs. {customers.length ? Math.round(totalSpentAll / customers.length).toLocaleString() : 0}
            </div>
            <div className="admin-stat-footer text-muted">Across all purchasing shoppers</div>
          </div>
        </div>
      </div>

      {/* Main Table Card */}
      <div className="admin-card">
        <div className="admin-card-header flex-wrap gap-3">
          <div className="position-relative" style={{ width: "300px" }}>
            <input
              type="text"
              className="form-control form-control-sm ps-4"
              placeholder="Search by name, email, phone, city..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ borderRadius: "8px" }}
            />
            <span className="position-absolute start-0 top-50 translate-middle-y ms-2 text-muted" style={{ fontSize: "11px" }}>
              <FaSearch />
            </span>
          </div>

          <span className="text-muted" style={{ fontSize: "13px" }}>
            Showing {filtered.length} customers
          </span>
        </div>

        <div className="table-responsive">
          {loading ? (
            <div className="p-5 text-center">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-5 text-center">
              <FaUsers size={36} className="text-muted mb-2" />
              <h6 className="fw-bold">No Customers Found</h6>
              <p className="text-muted mb-0" style={{ fontSize: "13px" }}>
                No customer records match your filter criteria.
              </p>
            </div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Customer Name</th>
                  <th>Contact Email</th>
                  <th>Phone Number</th>
                  <th>City / Region</th>
                  <th className="text-center">Total Orders</th>
                  <th className="text-end">Total Spent</th>
                  <th>Account Type</th>
                  <th>Last Active</th>
                  <th className="text-end">Orders</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((cust) => {
                  const open = selectedKey === cust.key;
                  return (
                  <React.Fragment key={cust.key}>
                  <tr style={{ background: open ? "#eff6ff" : undefined }}>
                    <td>
                      <span className="fw-bold text-dark d-block">{cust.name}</span>
                    </td>
                    <td>
                      <span className="text-muted d-flex align-items-center gap-1">
                        <FaEnvelope size={11} /> {cust.email}
                      </span>
                    </td>
                    <td>
                      <span className="text-muted d-flex align-items-center gap-1">
                        <FaPhone size={11} /> {cust.phone}
                      </span>
                    </td>
                    <td>
                      <span className="badge bg-light text-dark border">
                        <FaMapMarkerAlt size={10} className="me-1" /> {cust.city}
                      </span>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-primary-subtle text-primary border fw-bold px-2 py-1">
                        {cust.totalOrders} {cust.totalOrders === 1 ? "order" : "orders"}
                      </span>
                    </td>
                    <td className="text-end">
                      <span className="fw-bold text-dark">
                        Rs. {cust.totalSpent.toLocaleString()}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge ${cust.isRegistered ? "admin-badge-success" : "admin-badge-info"}`}>
                        {cust.isRegistered ? "Registered" : "Guest Shopper"}
                      </span>
                    </td>
                    <td>
                      <small className="text-muted">
                        {new Date(cust.lastOrderDate).toLocaleDateString("en-PK", { dateStyle: "medium" })}
                      </small>
                    </td>
                    <td className="text-end">
                      <button
                        type="button"
                        className={`btn btn-sm rounded-pill px-3 fw-semibold ${open ? "btn-dark" : "btn-outline-primary"}`}
                        onClick={() => openCustomer(cust)}
                      >
                        {open ? <FaChevronUp size={10} className="me-1" /> : <FaChevronDown size={10} className="me-1" />}
                        {open ? "Hide orders" : "Show orders"}
                      </button>
                    </td>
                  </tr>
                  {open && (
                    <tr ref={ordersPanel}>
                      <td colSpan="9" style={{ background: "#f8fafc", padding: "16px 20px" }}>
                        <div className="d-flex align-items-center justify-content-between mb-2">
                          <span className="fw-bold" style={{ fontSize: "13.5px" }}>
                            {cust.name} · {cust.orders.length} {cust.orders.length === 1 ? "order" : "orders"}
                          </span>
                        </div>
                        <div className="bg-white border rounded-3 overflow-hidden">
                          <table className="admin-table mb-0">
                            <thead>
                              <tr>
                                <th>Order</th>
                                <th>Date</th>
                                <th>City</th>
                                <th>Payment</th>
                                <th>Status</th>
                                <th className="text-center">Items</th>
                                <th className="text-end">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {cust.orders.map((ord) => {
                                const status = orderStatus(ord);
                                return (
                                  <tr key={ord.id}>
                                    <td>
                                      <Link to={`/admin/orders/${ord.id}`} className="fw-bold text-primary text-decoration-none">
                                        #{ord.id}
                                      </Link>
                                    </td>
                                    <td>
                                      <small className="text-muted">
                                        {new Date(ord.created_at).toLocaleDateString("en-PK", { dateStyle: "medium" })}
                                      </small>
                                    </td>
                                    <td>{ord.city || "—"}</td>
                                    <td>
                                      <span className="badge bg-light text-dark border">{paymentLabel(ord.payment_mode)}</span>
                                    </td>
                                    <td>
                                      <span className={`admin-badge ${status.badge}`}>{status.label}</span>
                                    </td>
                                    <td className="text-center">{ord.items_count || (ord.orderitems ? ord.orderitems.length : 0)}</td>
                                    <td className="text-end fw-bold">Rs. {Number(ord.total_amount || 0).toLocaleString()}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
